import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Eye,
  EyeOff,
  Shield,
  Mail,
  Lock,
  AlertTriangle,
  ScanFace,
  Wallet,
  Boxes,
  Users2,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';


const MODULES = [
  { icon: Wallet, label: 'Sales & Commissions' },
  { icon: Boxes, label: 'Contracts & Finance' },
  { icon: ScanFace, label: 'Face-ID Attendance' },
  { icon: Users2, label: 'HR & Assets' },
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [step, setStep] = useState('credentials'); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [otp, setOtp] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const redirectTo = location.state?.from?.pathname || '/';

  const completeLogin = async (access_token, refresh_token) => {
    localStorage.setItem('access_token', access_token);
    const me = await authService.getMe();
    login(me, { access_token, refresh_token });
    navigate(redirectTo, { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authService.login(email, password);

      if (res.requires_2fa) {
        setTempToken(res.temp_token);
        setStep('otp');
      } else {
        await completeLogin(res.access_token, res.refresh_token);
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authService.verify2FA(tempToken, otp);
      await completeLogin(res.access_token, res.refresh_token);
    } catch (err) {
      setError(err.message || 'Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      const res = await authService.resend2FA(tempToken);
      setTempToken(res.temp_token);
      setResendCooldown(30);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl shadow-slate-300/40 overflow-hidden flex min-h-[640px] border border-slate-100">

        {/* LEFT SIDE: Brand + SRS-based introduction */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-900 p-11 flex-col justify-between overflow-hidden">

          {/* Background office illustration (behind text) */}
          <svg
            className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.16]"
            viewBox="0 0 480 640"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* City skyline, back layer */}
            <rect x="20" y="120" width="46" height="220" fill="#ffffff" />
            <rect x="76" y="80" width="38" height="260" fill="#ffffff" />
            <rect x="124" y="150" width="52" height="190" fill="#ffffff" />
            <rect x="330" y="90" width="42" height="250" fill="#ffffff" />
            <rect x="382" y="140" width="36" height="200" fill="#ffffff" />
            <rect x="428" y="60" width="40" height="280" fill="#ffffff" />
            {/* window grid on towers */}
            {Array.from({ length: 8 }).map((_, row) =>
              Array.from({ length: 3 }).map((__, col) => (
                <rect
                  key={`w-${row}-${col}`}
                  x={30 + col * 12}
                  y={135 + row * 24}
                  width="6"
                  height="10"
                  fill="#5b21b6"
                />
              ))
            )}

            {/* Office desk / workstation, foreground */}
            <rect x="40" y="430" width="400" height="14" rx="3" fill="#ffffff" />
            <rect x="60" y="444" width="10" height="70" fill="#ffffff" />
            <rect x="410" y="444" width="10" height="70" fill="#ffffff" />

            {/* Monitor */}
            <rect x="150" y="360" width="110" height="72" rx="4" fill="#ffffff" />
            <rect x="160" y="370" width="90" height="52" rx="2" fill="#5b21b6" />
            <rect x="195" y="432" width="20" height="10" fill="#ffffff" />
            <rect x="180" y="442" width="50" height="6" rx="3" fill="#ffffff" />

            {/* Keyboard + mouse */}
            <rect x="150" y="450" width="80" height="14" rx="2" fill="#ffffff" />
            <circle cx="248" cy="457" r="6" fill="#ffffff" />

            {/* Second small monitor / tablet */}
            <rect x="280" y="392" width="60" height="42" rx="4" fill="#ffffff" />
            <rect x="286" y="398" width="48" height="28" rx="2" fill="#5b21b6" />

            {/* Desk plant */}
            <path d="M96 452 q6 -22 16 -22 q10 0 16 22 Z" fill="#ffffff" />
            <rect x="104" y="452" width="16" height="12" rx="2" fill="#ffffff" />

            {/* Face-ID scan frame, referencing the attendance feature */}
            <rect
              x="358"
              y="380"
              width="66"
              height="66"
              rx="10"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2.5"
            />
            <path d="M368 392 v-8 q0 -4 4 -4 h8" fill="none" stroke="#ffffff" strokeWidth="2.5" />
            <path d="M414 392 v-8 q0 -4 -4 -4 h-8" fill="none" stroke="#ffffff" strokeWidth="2.5" />
            <path d="M368 434 v8 q0 4 4 4 h8" fill="none" stroke="#ffffff" strokeWidth="2.5" />
            <path d="M414 434 v8 q0 4 -4 4 h-8" fill="none" stroke="#ffffff" strokeWidth="2.5" />
            <circle cx="391" cy="413" r="14" fill="none" stroke="#ffffff" strokeWidth="2" />
          </svg>

          {/* Purple gradient overlay to keep foreground text readable over the illustration */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-purple-600/95 via-purple-700/95 to-indigo-900/95" />

          {/* Decorative glow accents (on top of the illustration/overlay) */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-16 w-80 h-80 bg-indigo-400/20 rounded-full blur-3xl" />

          {/* Brand mark */}
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-black/10">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-white font-bold text-xl tracking-tight">SYNVEX</h1>
              <p className="text-purple-200 text-xs">Business Management Platform</p>
            </div>
          </div>

          {/* Introduction pulled from the SRS */}
          <div className="relative">
            <span className="inline-block text-[11px] font-semibold tracking-widest text-purple-200 uppercase mb-4 border border-white/20 rounded-full px-3 py-1">
              One platform, every department
            </span>

            <h2 className="text-white text-[28px] leading-tight font-bold mb-4">
              Run Sales, Finance, HR &amp; Operations from a single dashboard.
            </h2>

            <p className="text-purple-100/90 text-sm leading-relaxed mb-7">
              Synvex replaces scattered spreadsheets with one secure, role-based
              system — tracking sales commissions, client contracts and
              milestones, company expenses and payroll, company assets, and
              the full employee lifecycle, including AI-powered face
              recognition attendance.
            </p>

            {/* Module grid — mirrors the 4 core domains from the SRS scope */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {MODULES.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2.5 bg-white/10 backdrop-blur rounded-xl px-3.5 py-3 border border-white/10"
                >
                  <Icon className="w-4 h-4 text-white shrink-0" />
                  <span className="text-white text-xs font-medium leading-snug">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Key stats, straight from the SRS (roles, accuracy target) */}
            <div className="flex items-center gap-6 border-t border-white/15 pt-5 justify-center">
              <div>
                <p className="text-white text-xl font-bold leading-none">6</p>
                <p className="text-purple-200 text-[11px] mt-1">User roles</p>
              </div>
              <div className="w-px h-8 bg-white/15" />
              <div>
                <p className="text-white text-xl font-bold leading-none">95%+</p>
                <p className="text-purple-200 text-[11px] mt-1">Face-ID accuracy</p>
              </div>
              <div className="w-px h-8 bg-white/15" />
              <div>
                <p className="text-white text-xl font-bold leading-none">AES-256</p>
                <p className="text-purple-200 text-[11px] mt-1">Data encryption</p>
              </div>
            </div>
          </div>

          <p className="relative text-purple-300 text-xs mt-11 text-center">© 2021 Synvex Private Limited. All rights reserved.</p>
        </div>

        {/* RIGHT SIDE: Dynamic Form Side */}
        <div className="w-full lg:w-1/2 p-12 flex flex-col justify-center">
          <div className="max-w-sm mx-auto w-full">

            {/* Responsive Mobile Header */}
            <div className="lg:hidden flex items-center gap-2 mb-8">
              <Shield className="w-7 h-7 text-purple-600" />
              <span className="font-bold text-xl text-slate-800">SYNVEX</span>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 mb-6 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {step === 'credentials' && (
              <>
                <h2 className="text-2xl font-bold text-slate-800 mb-1">Welcome back</h2>
                <p className="text-slate-500 text-sm mb-8">Sign in to your account</p>

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@synvex.io"
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <a href="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                      Forgot password?
                    </a>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold text-sm transition disabled:opacity-60 flex items-center justify-center gap-2 group"
                  >
                    {loading ? 'Signing in...' : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {step === 'otp' && (
              <>
                <h2 className="text-2xl font-bold text-slate-800 mb-1">Verification Required</h2>
                <p className="text-slate-500 text-sm mb-8">Enter the 6-digit code we sent to your email.</p>

                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-center text-xl tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                      placeholder="000000"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold text-sm transition disabled:opacity-60"
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                </form>

                <div className="mt-6 text-center text-sm text-slate-500">
                  Didn't get a code?{' '}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0}
                    className="text-purple-600 font-medium hover:underline disabled:text-slate-400 disabled:no-underline"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setStep('credentials'); setOtp(''); setError(''); }}
                  className="mt-3 w-full text-center text-sm text-slate-400 hover:text-slate-600"
                >
                  ← Back to login
                </button>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;